// TypeScript types for the /manufacturing dashboard surface.
// Mirror the Pydantic models in apps/dashboard-backend/models/manufacturing_dashboard.py.

export interface ManufacturingAccessResponse {
  has_access: boolean;
}

export interface SyncStatusEntry {
  // 'schema_name' (not 'schema') avoids shadowing Pydantic's reserved
  // BaseModel.schema method on the backend.
  schema_name: 'manufacturing' | 'onshape';
  table_name: string;
  status: 'running' | 'success' | 'failed';
  started_at: string;
  finished_at: string | null;
  rows_inserted: number;
  error: string | null;
}

export interface ManufacturingSyncStatus {
  entries: SyncStatusEntry[];
  latest_at: string | null;
}

export interface ManufacturingProduct {
  id: string;
  odoo_id: number;
  default_code: string | null;
  name: string;
  type: string | null;
  uom: string | null;
  active: boolean;
  is_manufactured: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  synced_at: string;
}

export interface ManufacturingWorkCenter {
  id: string;
  odoo_id: number;
  name: string;
  code: string | null;
  active: boolean;
  capacity: number | null;
  oee_target: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  synced_at: string;
}

export interface BomLine {
  id: string;
  odoo_id: number;
  bom_id: string;
  product_id: string;
  product_qty: number;
  sequence: number;
  metadata?: Record<string, unknown>;
}

export interface BomWithLines {
  id: string;
  odoo_id: number;
  product_id: string;
  code: string | null;
  product_qty: number;
  type: 'normal' | 'phantom';
  active: boolean;
  metadata: Record<string, unknown>;
  bom_lines: BomLine[];
}

export interface WorkOrderInline {
  id: string;
  odoo_id: number;
  work_center_id: string | null;
  name: string;
  state: string;
  duration_expected: number | null;
  duration: number | null;
  date_planned_start: string | null;
  date_planned_finished: string | null;
  date_start: string | null;
  date_finished: string | null;
}

export interface ProductionOrderWithWorkOrders {
  id: string;
  odoo_id: number;
  name: string;
  product_id: string;
  bom_id: string | null;
  product_qty: number;
  qty_produced: number;
  state: string;
  date_planned_start: string | null;
  date_planned_finished: string | null;
  date_start: string | null;
  date_finished: string | null;
  work_orders: WorkOrderInline[];
}

export interface OnshapeDocument {
  id: string;
  onshape_id: string;
  name: string;
  default_workspace_id: string | null;
  owner_type: string | null;
  owner_id: string | null;
  created_at_remote: string | null;
  modified_at_remote: string | null;
  thumbnail_url: string | null;
  // First non-null pdf_href across this document's assemblies. Surfaces
  // the assembly work-instruction PDF on the BomGraph node.
  pdf_href: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  synced_at: string;
}

export interface OnshapePart {
  id: string;
  part_number: string | null;
  name: string;
  revision: string | null;
  state: string | null;
  material: string | null;
  mass_kg: number | null;
  thumbnail_url: string | null;
}

/**
 * Per-default-code lookup payload from
 * `GET /api/manufacturing/onshape/parts-by-code`. Replaces the old
 * substring-on-document-name heuristic with the same exact-equality
 * join the drift view uses.
 *
 * Codes that don't resolve are simply absent from the response.
 */
export interface PartsByCodeEntry {
  // null for an assembly row resolved via the drawing-suffix fallback (no
  // onshape.parts row exists for assemblies). Not used by the chips.
  part_id: string | null;
  part_number: string;
  onshape_doc_id: string | null;
  workspace_id: string | null;
  element_id: string | null;
  // Preferred work-instruction surface (engineering drawing). When present
  // the FE chip opens the backend proxy URL constructed from
  // `drawing_element_id` rather than this raw onshape webapp link.
  drawing_href: string | null;
  // Fallback work-instruction surface (PDF BLOB attached to an assembly).
  pdf_href: string | null;
  // 24-hex element id used by the FE to construct the proxy URL.
  drawing_element_id: string | null;
  // 24-hex element id of the work-instruction drawing (tab name flagged "WI" /
  // "Work Instruction"), if the part's document has one. The dedicated "WI"
  // chip builds its proxy URL from this; null => no WI (chip greyed). Heuristic
  // match, latest from the main workspace (may be work-in-progress).
  wi_drawing_element_id: string | null;
  // The WI drawing tab name, shown in the WI chip tooltip. Null when no WI.
  wi_drawing_name: string | null;
  // Deep-link to the part's parent document on cad.onshape.com. Used by
  // the (i) chip (Onshape webapp login required).
  onshape_url: string | null;
}

export type PartsByCodeResponse = Record<string, PartsByCodeEntry>;

// ---------------------------------------------------------------------------
// Per-part drill-down (Phase 5)
// ---------------------------------------------------------------------------

export interface MatchedVariant {
  variant_id: string;
  product_id: string;
  default_code: string;
  name: string;
}

export interface MOHistoryRow {
  mo_id: string;
  mo_name: string;
  mo_state: string;
  qty_planned: number;
  qty_produced: number;
  date_planned_start: string | null;
  date_planned_finished: string | null;
  role: 'output' | 'component' | string;
  parent_product_name: string | null;
}

export interface PartDetail {
  part_id: string;
  onshape_part_id: string;
  part_number: string | null;
  name: string;
  revision: string | null;
  state: string | null;
  material: string | null;
  mass_kg: number | null;
  thumbnail_url: string | null;
  onshape_url: string | null;
  drift_status: 'matched' | 'missing_in_odoo' | string;
  matched_variant: MatchedVariant | null;
  mos: MOHistoryRow[];
  mos_truncated: boolean;
}

// ---------------------------------------------------------------------------
// Simulate-order (View B)
// ---------------------------------------------------------------------------

export interface SimulationRequest {
  product_id: string;
  quantity: number;
}

export interface SimulationMissingPart {
  code: string | null;
  name: string;
  required: number;
  available: number;
  shortfall: number;
}

export type SimulationStatus = 'green' | 'yellow' | 'red';

export interface SimulationNode {
  id: string;
  product_id: string;
  name: string;
  code: string | null;
  qty_required: number;
  available: number | null;
  is_leaf: boolean;
  is_phantom: boolean;
  status: SimulationStatus;
  children: SimulationNode[];
  missing_parts: SimulationMissingPart[];
}

export interface SimulationReport {
  root: SimulationNode;
  truncated_at: string | null;
  total_missing_parts: number;
  stock_synced_at: string | null;
}

// Production-order kanban columns in the order Odoo's MRP UI shows them.
export const PRODUCTION_STATE_COLUMNS: ReadonlyArray<{ state: string; label: string }> = [
  { state: 'draft', label: 'Draft' },
  { state: 'confirmed', label: 'Confirmed' },
  { state: 'progress', label: 'In Progress' },
  { state: 'to_close', label: 'To Close' },
  { state: 'done', label: 'Done' },
  { state: 'cancel', label: 'Cancelled' },
];

// ---------------------------------------------------------------------------
// Drift view (Phase 4)
// ---------------------------------------------------------------------------

export interface DriftRow {
  // The shared join-key (Odoo default_code or Onshape part_number).
  code: string;
  name: string | null;
  // Free-form per-category context. Documented shapes:
  //   odoo_only:          {}
  //   onshape_only:       { revision: string | null }
  //   revision_mismatch:  { odoo_revision, onshape_revision }
  //   hierarchy_mismatch: { parent_code, kind: 'missing' | 'extra' }
  detail: Record<string, unknown>;
}

export interface DriftCategory {
  rows: DriftRow[];
  truncated: boolean;
}

export type DriftCategoryKey =
  | 'odoo_only'
  | 'onshape_only'
  | 'revision_mismatch'
  | 'hierarchy_mismatch';

export interface DriftReport {
  odoo_only: DriftCategory;
  onshape_only: DriftCategory;
  revision_mismatch: DriftCategory;
  hierarchy_mismatch: DriftCategory;
}
